/**
 * WorkspacesPage — list + management entry point at /admin/workspaces.
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Pencil, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { listWorkspaces } from "@/workspace/cloud";
import { useHasPermission } from "@/hooks/rbac/useUserPermissions";

export default function WorkspacesPage() {
  const navigate = useNavigate();
  const { hasPermission } = useHasPermission("settings", "edit");
  const { data: workspaces, isLoading, error } = useQuery({
    queryKey: ["workspace_views"],
    queryFn: listWorkspaces,
  });

  if (!hasPermission) {
    return (
      <div className="px-6 py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              You don't have permission to manage workspaces.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Workspaces</h1>
          <p className="text-sm text-muted-foreground">
            Curate the console UX for each role. Permissions still gate access — workspaces
            only narrow what's shown by default.
          </p>
        </div>
        <Button onClick={() => navigate("/admin/workspaces/new")}>
          <Plus className="h-4 w-4 mr-2" />
          New workspace
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading workspaces…</p>
      ) : error ? (
        <p className="text-sm text-destructive">Failed to load workspaces.</p>
      ) : !workspaces || workspaces.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No workspaces yet. Create one to give a role its own curated console.
            </p>
            <Button onClick={() => navigate("/admin/workspaces/new")}>
              <Plus className="h-4 w-4 mr-2" />
              Create workspace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {workspaces.map((ws) => (
            <Card key={ws.id} className="cursor-pointer hover:border-primary/40 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{ws.name}</CardTitle>
                    <CardDescription className="truncate">
                      {ws.description || "No description"}
                    </CardDescription>
                  </div>
                  {ws.isDefault ? (
                    <Badge variant="default" className="shrink-0">
                      Default
                    </Badge>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs space-y-1">
                  <div>
                    <span className="text-muted-foreground">Role:</span>{" "}
                    <span className="font-medium">{ws.roleName ?? "Unbound"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Lands on:</span>{" "}
                    <span className="font-mono">{ws.landingPath}</span>
                  </div>
                  <div className="flex gap-3">
                    <span>
                      <span className="text-muted-foreground">Nav:</span> {ws.navItems.length}
                    </span>
                    <span>
                      <span className="text-muted-foreground">Widgets:</span>{" "}
                      {ws.dashboardWidgets.length}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate(`/admin/workspaces/${ws.id}/edit`)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Edit
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
